/* eslint-disable react/prop-types */
import React, { useState } from 'react';

/**
 * Replicates SelectCardInput's exact visual style (same card, same TODAY input,
 * same pills) but includes a Comment textarea seamlessly inside the same card —
 * no double borders, no wrapper hacks.
 */
const MetricCardWithComment = ({
  id,
  title,
  pills = [],
  inputValue,
  onInputChange,
  disabled = false,
  /** When true, only the TODAY count is disabled; comment stays fully visible and editable (e.g. Station Reject summary). */
  inputDisabledOnly = false,
  highlightBorderColor = '#00C9A7',
  comment = '',
  onCommentChange,
  commentPlaceholder = 'Add a comment...',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputLocked = disabled || inputDisabledOnly;
  const dimWholeCard = disabled && !inputDisabledOnly;

  const activeBorder = highlightBorderColor;
  const border = isHovered && !inputLocked
    ? `1px solid ${activeBorder}`
    : '1px solid #E2E8F0';

  return (
    <div
      id={id}
      style={{
        backgroundColor: '#FFFFFF',
        border,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        opacity: dimWholeCard ? 0.6 : 1,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
      onMouseEnter={() => !inputLocked && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ── Metric row — exact SelectCardInput layout ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        minHeight: '90px',
      }}>
        {/* Left: title + pills */}
        <div style={{ flex: 1, paddingRight: '16px' }}>
          <span style={{
            fontSize: '16px',
            fontWeight: 600,
            color: inputLocked ? '#94A3B8' : '#334155',
            marginBottom: '8px',
            display: 'block',
            textAlign: 'left',
          }}>
            {title}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {pills.map((pill, i) => (
              <div
                key={i}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#F1F5F9',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {pill.label}
                <span style={{ marginLeft: '4px', color: '#334155', fontWeight: 700 }}>
                  {pill.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: TODAY input */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '60px',
        }}>
          <span style={{
            fontSize: '10px',
            fontWeight: 700,
            color: '#94A3B8',
            textTransform: 'uppercase',
            marginBottom: '4px',
            opacity: isHovered || isInputFocused ? 1 : 0,
            transition: 'opacity 0.15s',
          }}>
            TODAY
          </span>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => !inputLocked && onInputChange && onInputChange(e.target.value)}
            disabled={inputLocked}
            placeholder="0"
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            style={{
              width: '60px',
              height: '36px',
              borderRadius: '6px',
              border: `1px solid ${isInputFocused ? activeBorder : '#E2E8F0'}`,
              backgroundColor: '#FFFFFF',
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 700,
              color: inputLocked ? '#94A3B8' : '#334155',
              outline: 'none',
              cursor: inputLocked ? 'not-allowed' : 'text',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* ── Comment section — seamlessly inside the same card ── */}
      <div style={{
        borderTop: '1px solid #F1F5F9',
        padding: '10px 14px 14px 14px',
        background: '#FFFFFF',
      }}>
        <div style={{
          fontSize: '11px',
          fontWeight: 600,
          color: '#94A3B8',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: '6px',
          textAlign: 'left',
        }}>
          Comment
        </div>
        <textarea
          value={comment}
          onChange={(e) => onCommentChange && onCommentChange(e.target.value)}
          rows={2}
          placeholder={commentPlaceholder}
          style={{
            width: '100%',
            padding: '8px 10px',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            fontSize: '12px',
            color: '#374151',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            background: '#F8FAFC',
            lineHeight: 1.5,
            display: 'block',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = activeBorder;
            e.target.style.background = '#FFFFFF';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#E2E8F0';
            e.target.style.background = '#F8FAFC';
          }}
        />
      </div>
    </div>
  );
};

export default MetricCardWithComment;
